import React, { useState, useEffect } from 'react';
import { DocumentV2, DocumentPackage } from '../types';
import { packagesApi, CreatePackageRequest, UpdatePackageRequest } from '../services/documents';
import { formatLocalDate } from '../utils/date';

// ========================================
// PackageList Component
// ========================================

export interface PackageListProps {
  caseId: string;
  onSelectPackage: (pkg: DocumentPackage) => void;
  onCreatePackage: () => void;
}

/**
 * List of document packages for a case.
 * 
 * Requirements: 2.4.1.1, 2.4.2.1
 */
export const PackageList: React.FC<PackageListProps> = ({
  caseId,
  onSelectPackage,
  onCreatePackage,
}) => {
  const [packages, setPackages] = useState<DocumentPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await packagesApi.list(caseId);
      setPackages(result.packages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, [caseId]);

  return (
    <div data-testid="package-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Document Packages</h3>
        <button
          onClick={onCreatePackage}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#4caf50',
            color: 'white',
            cursor: 'pointer',
          }}
          data-testid="create-package-button"
        >
          + New Package
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
          Loading...
        </div>
      )}

      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          {error}
          <button
            onClick={loadPackages}
            style={{
              marginLeft: '12px',
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && packages.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            color: '#666',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
          }}
        >
          No packages yet. Create one to organize related documents.
        </div>
      )}

      {!loading && packages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {packages.map(pkg => (
            <div
              key={pkg.packageId}
              onClick={() => onSelectPackage(pkg)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: 'white',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              data-testid={`package-item-${pkg.packageId}`}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{pkg.name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {pkg.documentCount} document{pkg.documentCount !== 1 ? 's' : ''} • {formatLocalDate(pkg.createdAt)}
                </div>
              </div>
              <span style={{ color: '#666' }}>→</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================
// PackageDetail Component
// ========================================

export interface PackageDetailProps {
  packageId: string;
  onBack: () => void;
  onDocumentSelect: (doc: DocumentV2) => void;
  onRemoveDocument: (documentId: string) => Promise<void>;
  onDeletePackage: () => Promise<void>;
}

/**
 * Detailed view of a package showing its documents.
 * 
 * Requirements: 2.4.2.1, 2.4.2.2
 */
export const PackageDetail: React.FC<PackageDetailProps> = ({
  packageId,
  onBack,
  onDocumentSelect,
  onRemoveDocument,
  onDeletePackage,
}) => {
  const [pkg, setPkg] = useState<DocumentPackage | null>(null);
  const [documents, setDocuments] = useState<DocumentV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdatePackageRequest>({});
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPackage = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await packagesApi.get(packageId);
      setPkg(result.package);
      setDocuments(result.documents);
      setEditForm({ name: result.package.name, notes: result.package.notes || '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load package');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackage();
  }, [packageId]);

  const handleSave = async () => {
    if (!pkg) return;
    try {
      setSaving(true);
      const updated = await packagesApi.update(packageId, editForm);
      setPkg(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update package');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    try {
      setRemovingId(documentId);
      await onRemoveDocument(documentId);
      setDocuments(prev => prev.filter(d => d.documentId !== documentId));
      if (pkg) {
        setPkg({ ...pkg, documentCount: pkg.documentCount - 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove document');
    } finally {
      setRemovingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this package? Documents will not be deleted.')) {
      return;
    }
    try {
      setDeleting(true);
      await onDeletePackage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete package');
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
        Loading...
      </div>
    );
  }

  if (!pkg) {
    return (
      <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
        Package not found
      </div>
    );
  }

  return (
    <div data-testid="package-detail">
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#1976d2',
            cursor: 'pointer',
            padding: 0,
            marginBottom: '8px',
          }}
        >
          ← Back to Packages
        </button>

        {isEditing ? (
          <div>
            <input
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '8px',
              }}
              data-testid="edit-name-input"
            />
            <textarea
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              placeholder="Add notes..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '60px',
                resize: 'vertical',
              }}
              data-testid="edit-notes-input"
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={() => setIsEditing(false)}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: '0 0 4px' }}>{pkg.name}</h2>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {pkg.documentCount} document{pkg.documentCount !== 1 ? 's' : ''} • Created {formatLocalDate(pkg.createdAt)}
              </div>
              {pkg.notes && (
                <div style={{ marginTop: '8px', color: '#666' }}>{pkg.notes}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#f44336',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
                data-testid="delete-package-button"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      {/* Documents */}
      <h4 style={{ margin: '0 0 12px' }}>Documents</h4>
      {documents.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            color: '#666',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
          }}
        >
          No documents in this package
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {documents.map(doc => (
            <div
              key={doc.documentId}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: 'white',
              }}
              data-testid={`package-doc-${doc.documentId}`}
            >
              <div
                style={{ flex: 1, cursor: 'pointer' }}
                onClick={() => onDocumentSelect(doc)}
              >
                <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {formatFileSize(doc.fileSize)} • {formatLocalDate(doc.uploadedAt)}
                </div>
              </div>
              <button
                onClick={() => handleRemoveDocument(doc.documentId)}
                disabled={removingId === doc.documentId}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#f44336',
                  cursor: removingId === doc.documentId ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                }}
                data-testid={`remove-doc-${doc.documentId}`}
              >
                {removingId === doc.documentId ? '...' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================================
// CreatePackageModal Component
// ========================================

export interface CreatePackageModalProps {
  caseId: string;
  initialDocumentIds?: string[];
  onCreated: (pkg: DocumentPackage) => void;
  onClose: () => void;
}

/**
 * Modal for creating a new document package.
 * 
 * Requirements: 2.4.1.1
 */
export const CreatePackageModal: React.FC<CreatePackageModalProps> = ({
  caseId,
  initialDocumentIds = [],
  onCreated,
  onClose,
}) => {
  const [form, setForm] = useState<CreatePackageRequest>({
    caseId,
    name: '',
    notes: '',
    documentIds: initialDocumentIds,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Package name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const pkg = await packagesApi.create(form);
      onCreated(pkg);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create package');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="create-package-modal"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
        }}
      >
        <h2 style={{ margin: '0 0 16px' }}>Create Package</h2>

        {error && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Package Name *
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., IEP Meeting Documents"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
              autoFocus
              data-testid="package-name-input"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Notes (optional)
            </label>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Add notes about this package..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '80px',
                resize: 'vertical',
              }}
              data-testid="package-notes-input"
            />
          </div>

          {initialDocumentIds.length > 0 && (
            <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              {initialDocumentIds.length} document{initialDocumentIds.length !== 1 ? 's' : ''} will be added to this package
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: !form.name.trim() ? '#ccc' : '#1976d2',
                color: 'white',
                cursor: saving || !form.name.trim() ? 'not-allowed' : 'pointer',
              }}
              data-testid="create-package-submit"
            >
              {saving ? 'Creating...' : 'Create Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================================
// AddToPackageModal Component
// ========================================

export interface AddToPackageModalProps {
  documentId: string;
  documentName: string;
  caseId: string;
  onAdded: (packageId: string) => void;
  onClose: () => void;
}

/**
 * Modal for adding a document to an existing package.
 * 
 * Requirements: 2.4.1.2
 */
export const AddToPackageModal: React.FC<AddToPackageModalProps> = ({
  documentId,
  documentName,
  caseId,
  onAdded,
  onClose,
}) => {
  const [packages, setPackages] = useState<DocumentPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        const result = await packagesApi.list(caseId);
        setPackages(result.packages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load packages');
      } finally {
        setLoading(false);
      }
    };
    loadPackages();
  }, [caseId]);

  const handleAdd = async () => {
    if (!selectedPackageId) return;

    try {
      setAdding(true);
      setError(null);
      await packagesApi.addDocument(selectedPackageId, documentId);
      onAdded(selectedPackageId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add document to package');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="add-to-package-modal"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
        }}
      >
        <h2 style={{ margin: '0 0 8px' }}>Add to Package</h2>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Add "{documentName}" to a package
        </p>

        {error && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
            Loading packages...
          </div>
        ) : packages.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '24px',
              color: '#666',
              backgroundColor: '#fafafa',
              borderRadius: '4px',
            }}
          >
            No packages available. Create a package first.
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Select Package
            </label>
            <select
              value={selectedPackageId}
              onChange={(e) => setSelectedPackageId(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
              data-testid="package-select"
            >
              <option value="">Choose a package...</option>
              {packages.map(pkg => (
                <option key={pkg.packageId} value={pkg.packageId}>
                  {pkg.name} ({pkg.documentCount} docs)
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={adding}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: adding ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={adding || !selectedPackageId}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: !selectedPackageId ? '#ccc' : '#1976d2',
              color: 'white',
              cursor: adding || !selectedPackageId ? 'not-allowed' : 'pointer',
            }}
            data-testid="add-to-package-submit"
          >
            {adding ? 'Adding...' : 'Add to Package'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default {
  PackageList,
  PackageDetail,
  CreatePackageModal,
  AddToPackageModal,
};
